/**
 * Default dimensions for the chart
 */
export const DEFAULT_WIDTH = 800;
export const DEFAULT_HEIGHT = 700;

/**
 * Default padding around the chart
 * Note: left padding must accommodate Y-axis labels (background box width: 56px + margins)
 */
export const DEFAULT_PADDING = {
  top: 20,
  right: 20,
  bottom: 80,
  left: 80,
} as const;

/**
 * Animation defaults
 */
export const DEFAULT_ANIMATION_DURATION = 800;
export const STAGGER_INTERVAL = 40;

/**
 * Default border radius for top segment corners in pixels
 */
export const DEFAULT_BAR_RADIUS = 4;

/**
 * Default colors for chart elements
 * Using design system color tokens for consistency
 */
export const DEFAULT_COLORS = {
  gridLine: 'var(--color-border-neutral-decorative)',
  gridLineOpacity: 0.4,
  axisLabel: 'var(--color-foreground-neutral-bold)',
  axisLine: 'var(--color-border-neutral-medium)',
  tooltipBackground: 'var(--color-background-neutral-bold)',
  tooltipBorder: 'var(--color-border-neutral-medium)',
  tooltipText: 'var(--color-foreground-neutral-inverse)',
  tooltipSecondaryText: 'var(--color-foreground-neutral-subtle)',
} as const;

/**
 * Default styling values
 * Typography mapping:
 * - axisLabelFontSize (12px) + axisLabelFontWeight (500) = body2 (Typography kind)
 * - xAxisLabelFontSize (12px) + xAxisLabelFontWeight (600) = body2-bold (Typography kind)
 */
export const DEFAULT_STYLING = {
  axisLabelFontSize: 12,
  axisLabelFontWeight: 500,
  xAxisLabelFontSize: 12,
  xAxisLabelFontWeight: 600,
  tooltipFontSize: 12,
  tooltipTitleFontSize: 13,
  tooltipTitleFontWeight: 500,
  tooltipPadding: 10,
  tooltipBorderRadius: 8,
  tooltipLineHeight: 1.5,
  focusRingWidth: 2,
  focusRingColor: 'var(--color-border-focus)',
  focusRingOffset: 4,
  hoverTransitionDuration: 200,
  tooltipFadeInDuration: 150,
  tooltipSpacingFromBar: 12,
} as const;

/**
 * Geometry ratios for responsive scaling
 * All values are ratios relative to chart dimensions
 */
export const GEOMETRY_RATIOS = {
  // Bar width as percentage of available slot width
  barWidthRatio: 0.6,

  // Minimum bar width in pixels (enables horizontal scroll if needed)
  minBarWidth: 24,

  // Tooltip positioning
  tooltipOffsetFromBar: 12,
  tooltipOffsetFromBarBottom: 12,

  // Focus ring offset from element
  focusRingOffset: 4,
} as const;

/**
 * Tick interval options for Y-axis scale calculation
 * Algorithm selects the interval closest to (maxTotal / 4)
 * to produce 4-6 ticks including 0
 * Supports ranges from small (2) to large (5000+)
 */
export const TICK_INTERVAL_OPTIONS = [
  1, 2, 4, 5, 8, 10, 20, 25, 40, 50, 80, 100, 200, 250, 400, 500, 800, 1000,
  2000, 2500, 4000, 5000, 8000, 10000,
];

/**
 * Default segment colors palette
 * Used when a category doesn't have a color defined
 * Cycles through colors for additional segments
 */
export const DEFAULT_SEGMENT_COLORS = [
  '#3b82f6', // Blue
  '#10b981', // Green
  '#f59e0b', // Amber
  '#ef4444', // Red
  '#8b5cf6', // Purple
  '#ec4899', // Pink
  '#06b6d4', // Cyan
  '#14b8a6', // Teal
  '#f97316', // Orange
  '#495473', // Indigo
];

/**
 * Default colors for first and last segments
 * First segment (bottom): typically represents total/primary metric
 * Last segment (top): typically represents secondary metric
 */
export const DEFAULT_FIRST_SEGMENT_COLOR = '#3b82f6'; // Blue
export const DEFAULT_LAST_SEGMENT_COLOR = '#495473'; // Dark gray

export const DEFAULT_PAGE_SIZE = 12;
export const NO_DATA_TEXT = 'No data available';
